/**
 * V38.29 — Super Admin : Panneau Attestations CAP BATI+ CMI.
 *
 * Liste les devis signés (`status: signed`), permet la consultation
 * détaillée et la correction côte-à-côte avec audit log (motif obligatoire +
 * réémission optionnelle du PDF officiel).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileCheck, RefreshCw, Search, Edit3, History, Loader2, AlertTriangle,
  ExternalLink, ShieldCheck, Save, X,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/ccmi-cap-bati/cap-bati-plus-cmi/admin`;

const fmtEUR = (n) => Number(n || 0).toLocaleString('fr-FR', {
  style: 'currency', currency: 'EUR', minimumFractionDigits: 2,
});

const paymentBadge = (s) => {
  const map = {
    pending: 'bg-amber-100 text-amber-800',
    partial_paid: 'bg-blue-100 text-blue-800',
    paid: 'bg-emerald-100 text-emerald-800',
    failed: 'bg-red-100 text-red-800',
  };
  return <Badge className={map[s] || 'bg-slate-100 text-slate-600'}>{s || '—'}</Badge>;
};

export default function CapBatiAttestationsPanel() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [onlyCorrected, setOnlyCorrected] = useState(false);

  // Modal correction
  const [corrDialog, setCorrDialog] = useState({ open: false, quote: null });
  const [corrForm, setCorrForm] = useState({
    constructor_email: '', project_reference: '', subject_label: '', notes: '',
    reason: '', reissue_pdf: true,
  });
  const [submitting, setSubmitting] = useState(false);

  // Modal audit log
  const [auditDialog, setAuditDialog] = useState({ open: false, quoteId: null, data: null, loading: false });

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (search.trim()) params.search = search.trim();
      if (onlyCorrected) params.only_corrected = true;
      const r = await axios.get(`${API}/attestations`, { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch {
      toast.error('Erreur chargement attestations');
    } finally {
      setLoading(false);
    }
  }, [search, onlyCorrected]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/attestations`, { params: { limit: 100 } });
        if (cancelled) return;
        setItems(r.data.items || []);
        setTotal(r.data.total || 0);
      } catch {
        if (!cancelled) toast.error('Erreur chargement attestations');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const openCorrection = (quote) => {
    setCorrForm({
      constructor_email: quote.constructor_email || '',
      project_reference: quote.project_reference || '',
      subject_label: quote.subject_label || '',
      notes: quote.notes || '',
      reason: '',
      reissue_pdf: true,
    });
    setCorrDialog({ open: true, quote });
  };

  const submitCorrection = async () => {
    const quote = corrDialog.quote;
    if (!quote) return;
    if (!corrForm.reason || corrForm.reason.length < 5) {
      toast.error('Le motif est obligatoire (min 5 caractères)');
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        reason: corrForm.reason,
        reissue_pdf: !!corrForm.reissue_pdf,
      };
      // Envoyer seulement les champs qui ont changé
      ['constructor_email', 'project_reference', 'subject_label', 'notes'].forEach((f) => {
        if ((quote[f] || '') !== (corrForm[f] || '')) body[f] = corrForm[f];
      });
      const r = await axios.patch(`${API}/attestations/${quote.id}`, body);
      const fields = r.data.fields_changed || [];
      toast.success(
        r.data.correction?.reissued_pdf
          ? `Correction enregistrée (${fields.length} champ(s)) + PDF réémis`
          : `Correction enregistrée (${fields.length} champ(s))`,
      );
      setCorrDialog({ open: false, quote: null });
      await fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec correction');
    } finally {
      setSubmitting(false);
    }
  };

  const openAudit = async (quote) => {
    setAuditDialog({ open: true, quoteId: quote.id, data: null, loading: true });
    try {
      const r = await axios.get(`${API}/attestations/${quote.id}/audit-log`);
      setAuditDialog({ open: true, quoteId: quote.id, data: r.data, loading: false });
    } catch {
      toast.error('Erreur chargement audit log');
      setAuditDialog({ open: false, quoteId: null, data: null, loading: false });
    }
  };

  const currentQuote = corrDialog.quote;

  return (
    <div className="space-y-4" data-testid="cap-bati-attestations-panel">
      <Card>
        <CardHeader className="bg-emerald-50 rounded-t-lg">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileCheck className="h-6 w-6 text-emerald-700" />
                Attestations CAP BATI+ CMI signées ({total})
              </CardTitle>
              <CardDescription>
                Devis signés, correction côte-à-côte avec audit log + réémission PDF officiel.
              </CardDescription>
            </div>
            <Button
              size="sm" variant="outline" onClick={fetchItems} disabled={loading}
              data-testid="attestations-refresh-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-4 space-y-3">
          <div className="flex gap-2 items-end flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <Label htmlFor="attest_search">Recherche (référence, constructeur, projet)</Label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="attest_search" className="pl-8"
                  value={search} onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchItems()}
                  placeholder="CAP-BATI-CMI-... ou email@..."
                  data-testid="attestations-search-input"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <Checkbox
                checked={onlyCorrected}
                onCheckedChange={(c) => setOnlyCorrected(!!c)}
                data-testid="attestations-only-corrected"
              />
              Avec corrections uniquement
            </label>
            <Button onClick={fetchItems} data-testid="attestations-search-btn">
              <Search className="h-4 w-4 mr-1" /> Filtrer
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-8 text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Chargement…
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm" data-testid="attestations-empty">
              Aucune attestation signée pour le moment.
            </div>
          ) : (
            <div className="overflow-x-auto border rounded">
              <table className="w-full text-xs" data-testid="attestations-table">
                <thead className="bg-slate-100 text-slate-700">
                  <tr>
                    <th className="px-2 py-2 text-left">Référence devis</th>
                    <th className="px-2 py-2 text-left">Document officiel</th>
                    <th className="px-2 py-2 text-left">Projet</th>
                    <th className="px-2 py-2 text-left">Constructeur</th>
                    <th className="px-2 py-2 text-right">Total TTC</th>
                    <th className="px-2 py-2 text-left">Paiement</th>
                    <th className="px-2 py-2 text-left">Signé le</th>
                    <th className="px-2 py-2 text-left">Corrections</th>
                    <th className="px-2 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((q) => {
                    const corrCount = (q.admin_corrections || []).length;
                    return (
                      <tr
                        key={q.id} className="border-t hover:bg-slate-50"
                        data-testid={`attestation-row-${q.id}`}
                      >
                        <td className="px-2 py-2 font-mono">{q.reference}</td>
                        <td className="px-2 py-2 font-mono text-emerald-700">
                          {q.signed_document_reference || '—'}
                        </td>
                        <td className="px-2 py-2">{q.project_reference || '—'}</td>
                        <td className="px-2 py-2 truncate max-w-[160px]">{q.constructor_email || '—'}</td>
                        <td className="px-2 py-2 text-right font-semibold">{fmtEUR(q.amount_total_ttc)}</td>
                        <td className="px-2 py-2">{paymentBadge(q.payment_status)}</td>
                        <td className="px-2 py-2 text-slate-500">
                          {q.signed_at ? new Date(q.signed_at).toLocaleDateString('fr-FR') : '—'}
                        </td>
                        <td className="px-2 py-2">
                          {corrCount > 0 ? (
                            <Badge className="bg-amber-100 text-amber-800 font-semibold">{corrCount} corr.</Badge>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-right whitespace-nowrap">
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => openCorrection(q)}
                            title="Corriger côte-à-côte"
                            data-testid={`attestation-correct-${q.id}`}
                          >
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => openAudit(q)}
                            title="Historique audit"
                            data-testid={`attestation-audit-${q.id}`}
                          >
                            <History className="h-4 w-4" />
                          </Button>
                          {q.signed_verification_url && (
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => window.open(q.signed_verification_url, '_blank')}
                              title="Page de vérification publique"
                              data-testid={`attestation-verify-${q.id}`}
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal correction côte-à-côte */}
      <Dialog
        open={corrDialog.open}
        onOpenChange={(o) => !o && setCorrDialog({ open: false, quote: null })}
      >
        <DialogContent className="max-w-4xl" data-testid="correction-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="h-5 w-5 text-emerald-700" />
              Correction côte-à-côte
              {currentQuote && (
                <span className="font-mono text-sm text-slate-500 ml-2">
                  {currentQuote.reference}
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              Modifiez les méta-données puis indiquez un motif. La signature
              reste valide. Les coefficients et montants sont figés au snapshot.
            </DialogDescription>
          </DialogHeader>

          {currentQuote && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Colonne ORIGINAL */}
              <div className="border rounded p-3 bg-slate-50">
                <p className="text-xs font-bold uppercase text-slate-500 mb-2">Original (signé)</p>
                <div className="space-y-2 text-sm" data-testid="correction-original-column">
                  <div>
                    <span className="text-slate-500 text-xs">Email constructeur :</span>
                    <div className="font-mono">{currentQuote.constructor_email || '—'}</div>
                  </div>
                  <div>
                    <span className="text-slate-500 text-xs">Référence projet :</span>
                    <div className="font-mono">{currentQuote.project_reference || '—'}</div>
                  </div>
                  <div>
                    <span className="text-slate-500 text-xs">Libellé :</span>
                    <div>{currentQuote.subject_label || '—'}</div>
                  </div>
                  <div>
                    <span className="text-slate-500 text-xs">Notes :</span>
                    <div className="text-xs italic">{currentQuote.notes || '—'}</div>
                  </div>
                  <div className="text-xs text-slate-500 pt-2 border-t">
                    Total TTC : <strong>{fmtEUR(currentQuote.amount_total_ttc)}</strong><br/>
                    Document : <span className="font-mono">{currentQuote.signed_document_reference || '—'}</span>
                  </div>
                </div>
              </div>

              {/* Colonne CORRECTION */}
              <div className="border rounded p-3 border-emerald-300 bg-emerald-50">
                <p className="text-xs font-bold uppercase text-emerald-700 mb-2">Correction</p>
                <div className="space-y-2" data-testid="correction-edit-column">
                  <div>
                    <Label htmlFor="corr_email" className="text-xs">Email constructeur</Label>
                    <Input
                      id="corr_email" value={corrForm.constructor_email}
                      onChange={(e) => setCorrForm({ ...corrForm, constructor_email: e.target.value })}
                      data-testid="corr-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="corr_proj" className="text-xs">Référence projet</Label>
                    <Input
                      id="corr_proj" value={corrForm.project_reference}
                      onChange={(e) => setCorrForm({ ...corrForm, project_reference: e.target.value })}
                      data-testid="corr-project-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="corr_label" className="text-xs">Libellé</Label>
                    <Input
                      id="corr_label" value={corrForm.subject_label}
                      onChange={(e) => setCorrForm({ ...corrForm, subject_label: e.target.value })}
                      data-testid="corr-label-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="corr_notes" className="text-xs">Notes</Label>
                    <Textarea
                      id="corr_notes" rows={2} value={corrForm.notes}
                      onChange={(e) => setCorrForm({ ...corrForm, notes: e.target.value })}
                      data-testid="corr-notes-input"
                    />
                  </div>
                </div>
              </div>

              {/* Bandeau motif (full-width) */}
              <div className="md:col-span-2 border-t pt-3 space-y-2">
                <Label htmlFor="corr_reason" className="text-sm font-semibold text-red-700">
                  Motif de la correction (obligatoire, ≥ 5 caractères)
                </Label>
                <Textarea
                  id="corr_reason" rows={2} value={corrForm.reason}
                  onChange={(e) => setCorrForm({ ...corrForm, reason: e.target.value })}
                  placeholder="Ex: Coquille dans l'email constructeur signalée par le client le 14/02..."
                  data-testid="corr-reason-input"
                />
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={corrForm.reissue_pdf}
                    onCheckedChange={(c) => setCorrForm({ ...corrForm, reissue_pdf: !!c })}
                    data-testid="corr-reissue-pdf"
                  />
                  <ShieldCheck className="h-4 w-4 text-emerald-700" />
                  Réémettre le PDF officiel corrigé (recommandé)
                </label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline" onClick={() => setCorrDialog({ open: false, quote: null })}
              data-testid="corr-cancel-btn"
            >
              <X className="h-4 w-4 mr-1" /> Annuler
            </Button>
            <Button
              onClick={submitCorrection}
              disabled={submitting || !corrForm.reason || corrForm.reason.length < 5}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="corr-submit-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
              Appliquer la correction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Audit Log */}
      <Dialog
        open={auditDialog.open}
        onOpenChange={(o) => !o && setAuditDialog({ open: false, quoteId: null, data: null, loading: false })}
      >
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="audit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" /> Historique audit
            </DialogTitle>
          </DialogHeader>
          {auditDialog.loading ? (
            <div className="text-center py-6">
              <Loader2 className="h-6 w-6 animate-spin inline" />
            </div>
          ) : auditDialog.data ? (
            <div className="space-y-4 text-sm">
              <div className="bg-slate-50 p-3 rounded">
                <p className="font-mono text-xs">{auditDialog.data.reference}</p>
                <p className="text-xs text-slate-500">
                  Signé : {auditDialog.data.signed_at && new Date(auditDialog.data.signed_at).toLocaleString('fr-FR')}<br/>
                  Payé : {auditDialog.data.paid_at && new Date(auditDialog.data.paid_at).toLocaleString('fr-FR')}
                </p>
              </div>
              <div>
                <p className="font-semibold mb-2">Corrections administratives ({(auditDialog.data.admin_corrections || []).length})</p>
                {(auditDialog.data.admin_corrections || []).length === 0 ? (
                  <p className="text-xs text-slate-400 italic">Aucune correction.</p>
                ) : (
                  <ul className="space-y-2">
                    {(auditDialog.data.admin_corrections || []).map((c) => (
                      <li key={c.id} className="border rounded p-3 bg-amber-50">
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-xs">{c.id}</span>
                          <span className="text-xs text-slate-500">
                            {c.ts && new Date(c.ts).toLocaleString('fr-FR')}
                          </span>
                        </div>
                        <p className="text-xs italic text-slate-700 my-1">"{c.reason}"</p>
                        <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                          <div>
                            <p className="text-red-700 font-semibold mb-1">Avant :</p>
                            <pre className="bg-white border p-1 rounded overflow-x-auto">{JSON.stringify(c.before, null, 1)}</pre>
                          </div>
                          <div>
                            <p className="text-emerald-700 font-semibold mb-1">Après :</p>
                            <pre className="bg-white border p-1 rounded overflow-x-auto">{JSON.stringify(c.after, null, 1)}</pre>
                          </div>
                        </div>
                        {c.reissued_pdf && (
                          <p className="text-xs text-emerald-700 mt-2 flex items-center gap-1">
                            <ShieldCheck className="h-3 w-3" />
                            PDF réémis : <span className="font-mono">{c.new_document_reference}</span>
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ) : (
            <p className="text-xs text-slate-400">
              <AlertTriangle className="h-4 w-4 inline mr-1" /> Aucune donnée.
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAuditDialog({ open: false, quoteId: null, data: null, loading: false })}
              data-testid="audit-close-btn"
            >
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
